import { API_BASE_URL } from "./index";

export type OnboardingStatus =
  | "not_started"
  | "in_progress"
  | "completed"
  | "skipped";

export interface OnboardingState {
  status: OnboardingStatus;
  currentStep: number;
  startedAt: string | null;
  completedAt: string | null;
  skippedAt: string | null;
  availableActions: string[];
}

export type Result<T, E = OnboardingApiError> =
  | { ok: true; value: T }
  | { ok: false; error: E };

export interface OnboardingApiError {
  message: string;
  code?: string;
  status: number;
}

export const ONBOARDING_STEPS = [
  "welcome",
  "first_calendar_connected",
  "second_calendar_connected",
  "first_sync",
] as const;

export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

interface OnboardingEnvelope {
  success: boolean;
  timestamp?: string;
  onboarding?: OnboardingState;
  error?: { message: string; code?: string };
}

const getAuthHeader = (): Record<string, string> => {
  const token = localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const parseEnvelope = async (
  response: Response,
): Promise<Result<OnboardingState>> => {
  let payload: OnboardingEnvelope | null = null;
  try {
    payload = (await response.json()) as OnboardingEnvelope;
  } catch (_err) {
    return {
      ok: false,
      error: { message: "Invalid response body", status: response.status },
    };
  }

  if (!response.ok || !payload?.success) {
    return {
      ok: false,
      error: {
        message: payload?.error?.message ?? `HTTP ${response.status}`,
        code: payload?.error?.code,
        status: response.status,
      },
    };
  }

  if (!payload.onboarding) {
    return {
      ok: false,
      error: {
        message: "Onboarding state missing from response",
        status: response.status,
      },
    };
  }

  return { ok: true, value: payload.onboarding };
};

export const fetchOnboardingState = async (
  userId: string,
  signal?: AbortSignal,
): Promise<Result<OnboardingState>> => {
  try {
    const response = await fetch(`${API_BASE_URL}/onboarding/${userId}`, {
      headers: { ...getAuthHeader() },
      signal,
    });
    return parseEnvelope(response);
  } catch (error) {
    if ((error as Error).name === "AbortError") {
      return { ok: false, error: { message: "aborted", status: 0 } };
    }
    return {
      ok: false,
      error: { message: (error as Error).message, status: 0 },
    };
  }
};

export const completeOnboardingStep = async (
  userId: string,
  step: OnboardingStep,
  signal?: AbortSignal,
): Promise<Result<OnboardingState>> => {
  try {
    const response = await fetch(`${API_BASE_URL}/onboarding/${userId}/step`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      body: JSON.stringify({ step }),
      signal,
    });
    return parseEnvelope(response);
  } catch (error) {
    if ((error as Error).name === "AbortError") {
      return { ok: false, error: { message: "aborted", status: 0 } };
    }
    return {
      ok: false,
      error: { message: (error as Error).message, status: 0 },
    };
  }
};

export const skipOnboarding = async (
  userId: string,
  signal?: AbortSignal,
): Promise<Result<OnboardingState>> => {
  try {
    const response = await fetch(`${API_BASE_URL}/onboarding/${userId}/skip`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      signal,
    });
    return parseEnvelope(response);
  } catch (error) {
    if ((error as Error).name === "AbortError") {
      return { ok: false, error: { message: "aborted", status: 0 } };
    }
    return {
      ok: false,
      error: { message: (error as Error).message, status: 0 },
    };
  }
};

export interface OAuthStateToken {
  state: string;
  expiresInMs: number;
}

interface OAuthStateEnvelope {
  success: boolean;
  state?: string;
  expiresInMs?: number;
  error?: { message: string; code?: string };
}

export const issueOAuthState = async (
  userId: string,
  redirectKey: "setup" | "dashboard",
  signal?: AbortSignal,
): Promise<Result<OAuthStateToken>> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/onboarding/${userId}/oauth-state`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify({ redirectKey }),
        signal,
      },
    );
    let payload: OAuthStateEnvelope | null = null;
    try {
      payload = (await response.json()) as OAuthStateEnvelope;
    } catch (_err) {
      return {
        ok: false,
        error: { message: "Invalid response body", status: response.status },
      };
    }
    if (!response.ok || !payload?.success || !payload.state) {
      return {
        ok: false,
        error: {
          message: payload?.error?.message ?? `HTTP ${response.status}`,
          code: payload?.error?.code,
          status: response.status,
        },
      };
    }
    return {
      ok: true,
      value: {
        state: payload.state,
        expiresInMs: payload.expiresInMs ?? 0,
      },
    };
  } catch (error) {
    if ((error as Error).name === "AbortError") {
      return { ok: false, error: { message: "aborted", status: 0 } };
    }
    return {
      ok: false,
      error: { message: (error as Error).message, status: 0 },
    };
  }
};

export const isOnboardingFinished = (state: OnboardingState): boolean =>
  state.status === "completed" || state.status === "skipped";

export const isOnboardingActive = (state: OnboardingState): boolean =>
  state.status === "not_started" || state.status === "in_progress";
